import { decodeJwt } from "jose";
import { calculatePKCECodeChallenge, generateRandomCodeVerifier, generateRandomNonce, generateRandomState } from "oauth4webapi";
import auth0_js from "auth0-js";
import { getAuth0ClientSettings } from "./awsSettings.mjs";
import { SessionStore } from "./storage/session-store.mjs";
import { mutate, query, queryClient, urlDecodeB64, urlEncodeB64 } from "./utils.mjs";
import { createLogger } from "./logger.mjs";
var auth_auth0_client_idTokenPayloadKeys = /*#__PURE__*/ function(idTokenPayloadKeys) {
    idTokenPayloadKeys["ansiraId"] = "ansira_id";
    idTokenPayloadKeys["ansiraUuid"] = "ansira_uuid";
    idTokenPayloadKeys["email"] = "email";
    idTokenPayloadKeys["emailVerified"] = "email_verified";
    idTokenPayloadKeys["familyName"] = "family_name";
    idTokenPayloadKeys["givenName"] = "given_name";
    idTokenPayloadKeys["name"] = "name";
    idTokenPayloadKeys["phoneNumber"] = "phone_number";
    idTokenPayloadKeys["phoneNumberVerified"] = "phone_number_verified";
    idTokenPayloadKeys["picture"] = "picture";
    idTokenPayloadKeys["postalCode"] = "postal_code";
    idTokenPayloadKeys["purinaAdsOptIn"] = "purina_ads_opt_in";
    idTokenPayloadKeys["smsTransactionTAndC"] = "sms_transaction_t_and_c";
    idTokenPayloadKeys["sub"] = "sub";
    idTokenPayloadKeys["termsAndConditions"] = "t_and_c";
    idTokenPayloadKeys["termsAndConditionsTimestamp"] = "t_and_c_timestamp";
    idTokenPayloadKeys["tokenUse"] = "token_use";
    return idTokenPayloadKeys;
}({});
var auth_auth0_client_accessTokenPayloadKeys = /*#__PURE__*/ function(accessTokenPayloadKeys) {
    accessTokenPayloadKeys["ansiraId"] = "ansira_id";
    accessTokenPayloadKeys["ansiraUuid"] = "ansira_uuid";
    accessTokenPayloadKeys["azp"] = "azp";
    accessTokenPayloadKeys["tokenUse"] = "token_use";
    return accessTokenPayloadKeys;
}({});
let auth0WebAuth;
const usedResponseUrls = {};
const createAuthClient = (config)=>{
    const authClientSettings = getAuth0ClientSettings(config);
    const logger = createLogger({
        logLevel: authClientSettings.logLevel
    });
    const defaultCookieOptions = {
        secure: config.session?.cookieSettings?.secure ?? false,
        sameSite: config.session?.cookieSettings?.sameSite ?? 'lax',
        path: config.session?.cookieSettings?.path ?? '/',
        domain: authClientSettings.cookieDomain ?? window.location.hostname
    };
    const { host } = new URL(`https://${authClientSettings.domain}`);
    const subdomain = host.split('.')[0];
    const accessCookieOptions = {
        name: config.session?.cookieSettings?.name ?? `__auth0.access.${subdomain}`,
        ...defaultCookieOptions
    };
    const idCookieOptions = {
        name: config.session?.cookieSettings?.name ?? `__auth0.id.${subdomain}`,
        ...defaultCookieOptions
    };
    const refreshCookieOptions = {
        name: config.session?.cookieSettings?.name ?? `__auth0.refresh.${subdomain}`,
        ...defaultCookieOptions
    };
    const { protocol } = new URL(authClientSettings.redirectUri);
    if ('https:' === protocol) {
        accessCookieOptions.secure = true;
        accessCookieOptions.sameSite = 'strict';
    }
    const accessTokenStore = new SessionStore({
        secret: authClientSettings.secret,
        cookieOptions: accessCookieOptions
    });
    const idTokenStore = new SessionStore({
        secret: authClientSettings.secret,
        cookieOptions: idCookieOptions
    });
    const refreshTokenStore = new SessionStore({
        secret: authClientSettings.secret,
        cookieOptions: refreshCookieOptions
    });
    if (!auth0WebAuth) auth0WebAuth = new auth0_js.WebAuth({
        domain: authClientSettings.domain,
        clientID: authClientSettings.clientId,
        cookieDomain: authClientSettings.cookieDomain
    });
    const getCurrentToken = async (tokenType = 'access')=>{
        if ('id' === tokenType) return {
            __raw: idTokenStore.getPlainText(),
            ...await idTokenStore.getEncryptedJWT() || {}
        };
        if ('refresh' === tokenType) return {
            __raw: refreshTokenStore.getPlainText()
        };
        return {
            __raw: accessTokenStore.getPlainText(),
            ...await accessTokenStore.getEncryptedJWT() || {}
        };
    };
    const codeExchangeForToken = async (code, state)=>{
        if (!code) {
            const { searchParams } = new URL(window.location.href);
            code = searchParams.get('code');
        }
        if (!code) throw new Error('code missing for token exchange');
        if (usedResponseUrls[code]) return;
        usedResponseUrls[code] = true;
        if (!state) {
            const { searchParams } = new URL(window.location.href);
            state = searchParams.get('state');
        }
        if (!state) throw new Error('state missing for token exchange');
        logger.debug(`Beginning code exchange for code: ${code}`);
        return new Promise((resolve, reject)=>{
            const transaction = auth0WebAuth.transactionManager.getStoredTransaction(state);
            if (!transaction) {
                history.pushState({}, '', '/');
                reject(new Error(`transaction missing for ${state}`));
            }
            const appState = JSON.parse(urlDecodeB64(transaction.appState));
            const code_verifier = appState.code_verifier;
            auth0WebAuth.client.oauthToken({
                code_verifier,
                code: code,
                grantType: 'authorization_code',
                redirectUri: authClientSettings.redirectUri
            }, async function(err, data) {
                if (err) return reject(err);
                logger.debug('codeExchangeForToken -> oauthToken()', data);
                accessTokenStore.setPlainText(data.accessToken, true);
                await accessTokenStore.setEncryptedJWT(decodeJwt(data.accessToken), true);
                if (data.idToken) {
                    if (authClientSettings.shouldSaveIdTokenCookie) idTokenStore.setPlainText(data.idToken, true);
                    await idTokenStore.setEncryptedJWT(decodeJwt(data.idToken), true);
                }
                if (data.refreshToken) refreshTokenStore.setPlainText(data.refreshToken, true);
                return resolve({
                    ...data,
                    oAuthState: appState
                });
            });
        });
    };
    const isAuthenticated = async ()=>accessTokenStore.getEncryptedJWT().then((token)=>!!token);
    const federatedSignIn = async (options = {})=>{
        const code_verifier = generateRandomCodeVerifier();
        const code_challenge = await calculatePKCECodeChallenge(code_verifier);
        const appState = options.appState ? urlEncodeB64(JSON.stringify({
            ...options.appState,
            code_verifier
        })) : urlEncodeB64(JSON.stringify({
            code_verifier
        }));
        const state = options.state || generateRandomState();
        const nonce = options.nonce || generateRandomNonce();
        const params = {
            appState,
            state,
            nonce,
            code_challenge,
            code_challenge_method: 'S256',
            responseType: options.responseType || 'code',
            scope: config.authorizationParams?.scope,
            audience: config.audience,
            redirectUri: authClientSettings.redirectUri,
            organization: options.organization || authClientSettings.organization
        };
        return auth0WebAuth.authorize({
            ...options,
            ...params
        });
    };
    const fetchUserAttributes = async ()=>query([
            'fetchUserAttributes'
        ], ()=>new Promise((resolve, reject)=>{
                logger.debug('query executing for fetchUserAttributes');
                const idToken = idTokenStore.getPlainText();
                const accessToken = accessTokenStore.getPlainText();
                if (idToken) idTokenStore.getEncryptedJWT().then((session)=>resolve(session));
                else if (accessToken) return auth0WebAuth.client.userInfo(accessToken, (err, userInfo)=>{
                    if (err) reject(err);
                    return resolve(userInfo);
                });
                else return reject('No active session found');
            }));
    const getSession = async ()=>mutate([
            'getSession'
        ], ()=>new Promise(async (resolve, reject)=>{
                const token = await accessTokenStore.getEncryptedJWT();
                const tokenExpirationTime = token?.exp ? token.exp : 0;
                const now = Math.floor(Date.now() / 1000);
                if (tokenExpirationTime - 180 > now) {
                    logger.debug('using cache for getSession');
                    const accessToken = accessTokenStore.getPlainText();
                    const refreshToken = refreshTokenStore.getPlainText();
                    const idToken = idTokenStore.getPlainText();
                    let idTokenAttributes;
                    if (idToken) idTokenAttributes = decodeJwt(idToken);
                    if (accessToken) {
                        const accessTokenAttributes = decodeJwt(accessToken);
                        return resolve({
                            accessToken,
                            accessTokenAttributes,
                            refreshToken,
                            idToken,
                            idTokenAttributes
                        });
                    }
                }
                const state = generateRandomState();
                const nonce = generateRandomNonce();
                logger.debug('Attempting silent auth... for getSession');
                return auth0WebAuth.checkSession({
                    state,
                    nonce,
                    responseType: 'token id_token',
                    redirectUri: authClientSettings.redirectUri,
                    scope: authClientSettings.authorizationParams.scope,
                    audience: authClientSettings.audience,
                    organization: authClientSettings.organization
                }, async (err, data)=>{
                    if (err) return reject(err);
                    logger.debug('checkSession()', data);
                    if (data.accessToken) {
                        accessTokenStore.setPlainText(data.accessToken);
                        const accessTokenAttributes = decodeJwt(data.accessToken);
                        await accessTokenStore.setEncryptedJWT(accessTokenAttributes);
                        data['accessTokenAttributes'] = accessTokenAttributes;
                    }
                    if (data.idToken && authClientSettings.shouldSaveIdTokenCookie) {
                        idTokenStore.setPlainText(data.idToken);
                        const idTokenAttributes = decodeJwt(data.idToken);
                        await idTokenStore.setEncryptedJWT(idTokenAttributes);
                        data['idTokenAttributes'] = idTokenAttributes;
                        delete data['idTokenPayload'];
                    }
                    if (data.refreshToken) refreshTokenStore.setPlainText(data.refreshToken);
                    return resolve(data);
                });
            }));
    const refreshUser = async ()=>mutate([
            'renewAuth'
        ], ()=>new Promise((resolve, reject)=>{
                logger.debug('mutate executing for renewAuth');
                return auth0WebAuth.renewAuth({
                    responseType: 'token id_token',
                    redirectUri: authClientSettings.redirectUri,
                    scope: authClientSettings.authorizationParams.scope,
                    audience: authClientSettings.audience
                }, async (err, data)=>{
                    if (err) return reject(err);
                    logger.debug('renewAuth()', data);
                    if (data.accessToken) {
                        accessTokenStore.setPlainText(data.accessToken);
                        await accessTokenStore.setEncryptedJWT(decodeJwt(data.accessToken));
                    }
                    if (data.idToken && authClientSettings.shouldSaveIdTokenCookie) {
                        idTokenStore.setPlainText(data.idToken);
                        await idTokenStore.setEncryptedJWT(decodeJwt(data.idToken));
                    }
                    if (data.refreshToken) refreshTokenStore.setPlainText(data.refreshToken);
                    return resolve(data);
                });
            }));
    const signUp = async (options = {})=>{
        const appState = options.appState ? urlEncodeB64(JSON.stringify({
            ...options.appState
        })) : urlEncodeB64(JSON.stringify({}));
        const state = options.state || generateRandomState();
        const nonce = options.nonce || generateRandomNonce();
        const params = {
            appState,
            state,
            nonce,
            responseType: options.responseType || 'code',
            scope: config.authorizationParams?.scope,
            audience: config.audience,
            redirectUri: authClientSettings.redirectUri,
            organization: options.organization ?? authClientSettings.organization
        };
        return auth0WebAuth.authorize({
            ...options,
            screen_hint: 'signup',
            ...params
        });
    };
    const signOut = async (returnTo, federated = false)=>{
        await Promise.all([
            accessTokenStore.delete(),
            idTokenStore.delete(),
            refreshTokenStore.delete(),
            queryClient.clear()
        ]);
        return auth0WebAuth.logout({
            returnTo,
            federated
        });
    };
    return {
        authClient: auth0WebAuth,
        getCurrentToken,
        codeExchangeForToken,
        isAuthenticated,
        federatedSignIn,
        fetchUserAttributes,
        getSession,
        refreshUser,
        signUp,
        signOut
    };
};
function getAuthTokenFromCookieHeaders(config, tokenType, headers) {
    const cookies = headers.get('cookie');
    const { host } = new URL(`https://${config.domain}`);
    const authSubdomain = host.split('.')[0];
    const auth0Cookie = cookies?.split(';').find((cookie)=>cookie.trim().startsWith(`__auth0.${tokenType}.${authSubdomain}`));
    const token = auth0Cookie?.split('=')[1];
    return token;
}
function getAuthTokenFromBrowserCookies(config, tokenType) {
    if ('undefined' == typeof document) return null;
    const cookies = document.cookie;
    const { host } = new URL(`https://${config.domain}`);
    const authSubdomain = host.split('.')[0];
    const auth0Cookie = cookies?.split(';').find((cookie)=>cookie.trim().startsWith(`__auth0.${tokenType}.${authSubdomain}`));
    const token = auth0Cookie?.split('=')[1];
    return token;
}
const isAccessToken = (payload)=>'token_use' in payload && 'access' === payload.token_use;
const isIdToken = (payload)=>'token_use' in payload && 'id' === payload.token_use;
function decodeToken(token) {
    const decodedToken = decodeJwt(token);
    if (isAccessToken(decodedToken)) return decodedToken;
    isIdToken(decodedToken);
    return decodedToken;
}
export { auth_auth0_client_accessTokenPayloadKeys as accessTokenPayloadKeys, createAuthClient, decodeToken, getAuthTokenFromBrowserCookies, getAuthTokenFromCookieHeaders, auth_auth0_client_idTokenPayloadKeys as idTokenPayloadKeys };
